import type { PresentOffersResponse, SessionUser } from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { OperatingGuardrails } from '../components/OperatingGuardrails';
import { formatCents, formatDate } from '../lib/format';

/**
 * Offer-menu BUILDER — pick priced bids into the seller menu (Tier-1 pass-through is enforced server-side),
 * set rank, PREVIEW the exact masked view the seller will see, then PRESENT (mint the seller link + send).
 */
export function OfferBuilderPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [presented, setPresented] = useState<PresentOffersResponse | null>(null);

  const builder = useQuery({ queryKey: ['offers', id], queryFn: () => api.offers.forSubmission(id), enabled: Boolean(id) });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['offers', id] });
  const add = useMutation({ mutationFn: (bidId: string) => api.offers.add(id, { bid_id: bidId }), onSuccess: invalidate });
  const withdraw = useMutation({ mutationFn: (offerId: string) => api.offers.withdraw(offerId), onSuccess: invalidate });
  const rerank = useMutation({ mutationFn: (v: { offerId: string; rank: number | null }) => api.offers.setRank(v.offerId, v.rank), onSuccess: invalidate });
  const present = useMutation({
    mutationFn: () => api.offers.present(id),
    onSuccess: async (res) => {
      setPresented(res);
      await invalidate();
    },
  });

  return (
    <Layout user={user}>
      {builder.isLoading ? (
        <p className="muted">Loading…</p>
      ) : builder.isError ? (
        <p className="error">{(builder.error as Error).message}</p>
      ) : builder.data ? (
        (() => {
          const b = builder.data;
          const notYetOffered = b.candidate_bids.filter((c) => !c.is_offered);
          return (
            <>
              <p>
                <Link to={`/submissions/${id}`} className="muted">
                  ← Submission
                </Link>
              </p>
              <div className="row-between">
                <h2>Offer menu builder</h2>
                <div className="inline">
                  <button onClick={() => present.mutate()} disabled={present.isPending || b.offers.length === 0}>
                    {present.isPending ? 'Presenting…' : b.presented ? 'Re-present (new link)' : 'Present to seller'}
                  </button>
                </div>
              </div>
              {present.isError ? <p className="error">{(present.error as Error).message}</p> : null}

              <OperatingGuardrails variant="offer" />

              {presented ? (
                <div className="card" style={{ background: '#f0f9ff' }}>
                  <h3>Presented — seller magic-link minted</h3>
                  <p>
                    {presented.offer_count} offer(s) · delivery <strong>{presented.send_status}</strong>
                    {presented.channel ? ` (${presented.channel})` : ''}
                  </p>
                  <p className="muted" style={{ wordBreak: 'break-all' }}>
                    Seller link (no login): <code>{presented.menu_url}</code>
                  </p>
                </div>
              ) : null}

              {/* CURATED OFFERS — with the Tier-1 pass-through visible (presented == bid) */}
              <div className="card">
                <h3>Curated offers ({b.offers.length})</h3>
                {b.offers.length === 0 ? (
                  <p className="muted">No offers yet. Add priced bids below.</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Presented</th>
                          <th>Bid</th>
                          <th>Dealer (operator view)</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.offers.map((o) => (
                          <tr key={o.id}>
                            <td>
                              <input
                                style={{ width: 48 }}
                                type="number"
                                defaultValue={o.rank ?? undefined}
                                onBlur={(e) => rerank.mutate({ offerId: o.id, rank: e.target.value ? Number(e.target.value) : null })}
                              />
                            </td>
                            <td>
                              <strong>{formatCents(o.presented_amount_cents)}</strong>
                            </td>
                            <td className={o.presented_amount_cents === o.bid_amount_cents ? 'muted' : 'error'}>
                              {formatCents(o.bid_amount_cents)}
                              {o.presented_amount_cents === o.bid_amount_cents ? ' ✓' : ' ✗ markup!'}
                            </td>
                            <td>
                              {o.dealer_business_name} <span className="muted">({o.dealer_state})</span>
                              {o.has_named_buyer ? <span className="pill conf-high">buyer</span> : null}
                            </td>
                            <td>
                              <span className="pill">{o.status}</span>
                              {o.seller_response ? <span className="muted"> · {o.seller_response}</span> : null}
                            </td>
                            <td>
                              {o.status === 'draft' || o.status === 'presented' ? (
                                <button className="ghost" onClick={() => withdraw.mutate(o.id)} disabled={withdraw.isPending}>
                                  Withdraw
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* CANDIDATE PRICED BIDS to add */}
              <div className="card">
                <h3>Priced bids available to add ({notYetOffered.length})</h3>
                {notYetOffered.length === 0 ? (
                  <p className="muted">No further priced bids. (Passes/no-bids can’t be presented.)</p>
                ) : (
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Amount</th>
                          <th>Type</th>
                          <th>Dealer</th>
                          <th>Received</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {notYetOffered.map((c) => (
                          <tr key={c.bid_id} className={c.has_named_buyer ? 'wedge-row' : ''}>
                            <td>
                              <strong>{formatCents(c.amount_cents)}</strong>
                            </td>
                            <td className="muted">{c.bid_type}</td>
                            <td>
                              {c.dealer_business_name} <span className="muted">({c.dealer_state})</span>
                              {c.has_named_buyer ? <span className="pill conf-high">buyer</span> : null}
                            </td>
                            <td className="muted">{formatDate(c.created_at)}</td>
                            <td>
                              <button onClick={() => add.mutate(c.bid_id)} disabled={add.isPending}>
                                Add to menu
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* MASKED SELLER PREVIEW — exactly what the seller sees (dealer identity hidden) */}
              <div className="card">
                <div className="row-between">
                  <h3>Seller preview — the masked menu</h3>
                  <span className="muted">dealer identity hidden until accept</span>
                </div>
                <p className="muted">
                  {b.seller_preview.vehicle.model_designation ?? '—'} {b.seller_preview.vehicle.model_year ?? ''}
                </p>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Offer</th>
                        <th>Dealer (seller’s view)</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.seller_preview.offers.map((o) => (
                        <tr key={o.offer_id}>
                          <td>
                            <strong>{formatCents(o.amount_cents)}</strong>
                          </td>
                          <td>
                            {o.dealer_business_name ?? (
                              <span className="muted">
                                {o.dealer_region} dealer{o.has_named_buyer ? ' · buyer-in-hand' : ''}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="pill">{o.status}</span>
                          </td>
                        </tr>
                      ))}
                      {b.seller_preview.offers.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="muted">
                            Nothing to preview yet.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          );
        })()
      ) : null}
    </Layout>
  );
}
